import { Trans, useTranslation } from 'react-i18next';

import CustomInput from '../../../../../shared/components/Inputs/CustomInput/CustomInput';
import { MIN_EMPLOYEES_FOR_QUOTES } from '../../../../../shared/constants/quotes';
import { INPUT_ACTION, QUOTE_HEADCOUNT, QUOTE_NAME, QUOTE_SEGMENT } from '../../../../../shared/constants/tagManager';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_QUOTES } from '../../../../../shared/translations/quotes';
import PhaseHeader from '../../components/PhaseHeader/PhaseHeader';
import './GroupCoverageSection.scss';

interface IGroupCoverageSectionProps {
  employees: string;
  employeesError?: string | null;
  employeesComplete: boolean;
  quoteName: string;
  segment: string;
  onEmployeesChange: (value: string) => void;
  onQuoteNameChange: (value: string) => void;
  onSegmentChange: (value: string) => void;
  minEmployees?: number;
}

const GroupCoverageSection = ({
  employees,
  employeesError,
  employeesComplete,
  quoteName,
  segment,
  onEmployeesChange,
  onQuoteNameChange,
  onSegmentChange,
  minEmployees = MIN_EMPLOYEES_FOR_QUOTES
}: IGroupCoverageSectionProps) => {
  const { t } = useTranslation();

  return (
    <section className="quote-group">
      <PhaseHeader
        number="1"
        title={t(TRANSLATIONS_QUOTES.create.group.title)}
        status={employeesComplete ? `✓ ${t(TRANSLATIONS_QUOTES.create.complete)}` : undefined}
      />

      <div className="quote-group__employees">
        <div className="quote-group__employees-header">
          <label htmlFor="quote-employees" className="quote-group__employees-label">
            {t(TRANSLATIONS_QUOTES.create.group.employees) + ' *'}
          </label>
          <span className="quote-group__tag">{t(TRANSLATIONS_QUOTES.create.group.employeesPrefilled)}</span>
        </div>
        <div className="quote-group__employees-row">
          <CustomInput
            id="quote-employees"
            type="number"
            min={1}
            maxLength={10}
            value={employees}
            onChange={(event) => {
              if (event.target.value.length <= 10) {
                onEmployeesChange(event.target.value);
              }
            }}
            containerClassName="quote-group__employees-input"
            onBlur={() => {
              TagManagerService.pushEvent(INPUT_ACTION, QUOTE_HEADCOUNT);
            }}
          />
          <p className="quote-group__employees-hint">{t(TRANSLATIONS_QUOTES.create.group.employeesHint)}</p>
        </div>
        {employeesError && (
          <p className="quote-group__employees-note">
            <Trans
              i18nKey={TRANSLATIONS_QUOTES.create.group.employeesMin}
              values={{ count: minEmployees }}
              components={{ b: <b /> }}
            />
          </p>
        )}
      </div>

      <div className="quote-group__optional">
        <div className="quote-group__optional-row">
          <CustomInput
            label={t(TRANSLATIONS_QUOTES.create.group.quoteName) + ' *'}
            placeholder={t(TRANSLATIONS_QUOTES.create.defaultName)}
            value={quoteName}
            onChange={(event) => onQuoteNameChange(event.target.value)}
            containerClassName="quote-group__field"
            onBlur={() => {
              TagManagerService.pushEvent(INPUT_ACTION, QUOTE_NAME);
            }}
          />
          <CustomInput
            label={t(TRANSLATIONS_QUOTES.create.group.segment)}
            placeholder={t(TRANSLATIONS_QUOTES.create.group.segmentPlaceholder)}
            value={segment}
            onChange={(event) => onSegmentChange(event.target.value)}
            containerClassName="quote-group__field"
            maxLength={100}
            onBlur={() => {
              TagManagerService.pushEvent(INPUT_ACTION, QUOTE_SEGMENT);
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default GroupCoverageSection;
